import { forwardRef } from 'react'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cx } from '../cx'
import { Icon, type IconName } from '../Icon'

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'buy'
  | 'quiet'
  | 'outline-strong'

export type ButtonSize = 'sm' | 'md' | 'lg'

const SIZE_CLASS: Record<ButtonSize, string> = { sm: 'btn-sm', md: '', lg: 'btn-lg' }

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  loading?: boolean
  icon?: IconName
  iconEnd?: IconName
  type?: 'button' | 'submit' | 'reset'
  /** Render as a router link. The visual is identical. */
  to?: string
  /**
   * The refusal is already known before submit, so the button becomes the
   * way out of it: «ثبت حساب بانکی»، «افزایش موجودی برای ادامه». It keeps
   * its variant and place, takes the fix's label, and goes to `to` or runs
   * `onClick` in place of its own action. Use it on a form's primary and on
   * a DecisionBar's leading decision alike.
   */
  fix?: ButtonFix | null
  children?: ReactNode
}

export interface ButtonFix {
  label: ReactNode
  to?: string
  onClick?: () => void
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    block,
    loading,
    icon,
    iconEnd,
    className,
    children,
    disabled,
    to,
    type = 'button',
    fix,
    ...rest
  },
  ref,
) {
  if (fix) {
    // The fix replaces the action: its label, its target, a forward chevron.
    // The button's own submit type and handler are dropped with it.
    children = fix.label
    to = fix.to
    iconEnd = 'chevron-left'
    type = 'button'
    disabled = false
    loading = false
    rest.onClick = fix.to ? undefined : fix.onClick
    className = cx('btn-fix', className)
  }
  const cls = cx(
    'btn',
    `btn-${variant}`,
    SIZE_CLASS[size],
    block && 'btn-block',
    loading && 'btn-loading',
    className,
  )
  const iconSize = size === 'sm' ? 'sm' : 'md'
  const content = (
    <>
      {icon && <Icon name={icon} size={iconSize} />}
      {children}
      {iconEnd && <Icon name={iconEnd} size={iconSize} />}
    </>
  )

  if (to && !disabled && !loading) {
    return (
      <Link className={cls} to={to} {...(rest as Record<string, unknown>)}>
        {content}
      </Link>
    )
  }

  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || loading} {...rest}>
      {content}
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'icon' | 'children' | 'iconEnd'> {
  icon: IconName
  /** Becomes the accessible name. An icon button without one is unusable. */
  label: string
  /** Draws the unread pip the topbar bell carries. */
  pip?: boolean
  /**
   * Draws the number itself in place of the pip, in Persian digits, «۹۹+»
   * past ninety nine. Nothing is drawn for zero. The label still carries the
   * count, since the badge is drawn for the eye.
   */
  count?: number
}

/** The badge's text: Persian digits, and «۹۹+» once it would need three. */
function countFa(n: number): string {
  const text = n > 99 ? '99+' : String(n)
  return text.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, pip, count, variant = 'ghost', size = 'md', className, ...rest },
  ref,
) {
  const counted = count !== undefined && count > 0
  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cx('btn-icon', className)}
      aria-label={label}
      title={label}
      style={pip || counted ? { position: 'relative' } : undefined}
      {...rest}
    >
      <Icon name={icon} size={size === 'sm' ? 'sm' : 'md'} />
      {counted ? (
        // Keyed by the number, so a new count lands with the rail's small pop.
        <span key={count} aria-hidden="true" className="btn-count">
          {countFa(count)}
        </span>
      ) : null}
      {pip && !counted && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            insetBlockStart: 5,
            insetInlineEnd: 5,
            inlineSize: 6,
            blockSize: 6,
            borderRadius: 99,
            background: 'var(--c-sell)',
          }}
        />
      )}
    </Button>
  )
})
